import { useMemo } from 'react';
import { MISSING, shelfLabel } from '../inventoryView';
import { useModalDialog } from '@/pages/fulfillment/components/useModalDialog';
import { kstMinute } from '@/utils/fulfillmentTime';
import type { StockTakeOrigin, StockTakeRound } from '@/infrastructure/FulfillmentAdminClient';

export type RoundTypeFilter = StockTakeOrigin | 'all';

export interface PastAuditLogsModalProps {
  rounds: StockTakeRound[];
  loading: boolean;
  /** Which kind of round the list is narrowed to; the server does the narrowing, this only shows the choice. */
  origin?: RoundTypeFilter;
  /** Whether the server holds rounds older than the ones shown. */
  hasMore?: boolean;
  onOriginChange?: (origin: RoundTypeFilter) => void;
  onLoadMore?: () => void;
  onClose: () => void;
}

/** One line of the log: an audit walk with every shelf it covered, or a single ✎ edit [SSD-7212]. */
export interface AuditLogEntry {
  key: string;
  origin: StockTakeOrigin;
  operator: string | null;
  /** When the first shelf of the walk was opened. */
  startedAt: string | null;
  /** When the last shelf of the walk was signed off; null while any shelf is still open or none was confirmed. */
  confirmedAt: string | null;
  shelves: StockTakeRound[];
  adjusted: number;
  /** One word when every shelf ended the same way, otherwise the tally of how each ended. */
  status: string;
}

const auditDate = (iso: string | null): string => (iso ? kstMinute(iso) : MISSING);

const TYPE_LABEL: Record<StockTakeOrigin, string> = { STOCK_AUDIT: 'Audit', MANUAL_ADJUST: '✎ Edit' };

/** A round from a server that predates the origin field is read as an audit, the way its adjustments were booked. */
const originOf = (round: StockTakeRound): StockTakeOrigin =>
  round.origin === 'MANUAL_ADJUST' ? 'MANUAL_ADJUST' : 'STOCK_AUDIT';

const minIso = (a: string | null, b: string | null): string | null => (a && b ? (a < b ? a : b) : (a ?? b));
const maxIso = (a: string | null, b: string | null): string | null => (a && b ? (a > b ? a : b) : (a ?? b));

const statusOf = (shelves: StockTakeRound[]): string => {
  const counts = new Map<string, number>();
  shelves.forEach((shelf) => counts.set(shelf.status, (counts.get(shelf.status) ?? 0) + 1));
  if (counts.size === 1) {
    return shelves[0]!.status;
  }
  return [...counts].map(([status, count]) => `${count} ${status.toLowerCase()}`).join(' · ');
};

/**
 * One row per audit: the shelves a walk opened share its `auditGroupId`, and a ✎ edit or a round from
 * before walks were grouped stands on its own. Rounds arrive newest-first, so an entry keeps the order
 * of its newest shelf; a walk whose shelves straddle a page shows the shelves read so far.
 */
export const groupRounds = (rounds: StockTakeRound[]): AuditLogEntry[] => {
  const entries = new Map<string, AuditLogEntry>();
  rounds.forEach((round) => {
    const key = round.auditGroupId ?? round.docId;
    const entry = entries.get(key);
    if (!entry) {
      entries.set(key, {
        key,
        origin: originOf(round),
        operator: round.operator,
        startedAt: round.startedAt,
        confirmedAt: round.confirmedAt,
        shelves: [round],
        adjusted: round.adjusted,
        status: round.status,
      });
      return;
    }
    entry.shelves.push(round);
    entry.operator = entry.operator ?? round.operator;
    entry.startedAt = minIso(entry.startedAt, round.startedAt);
    entry.confirmedAt = maxIso(entry.confirmedAt, round.confirmedAt);
    entry.adjusted += round.adjusted;
    entry.status = statusOf(entry.shelves);
  });
  // A walk with an open shelf has not finished, so it carries no confirmed stamp until the last shelf is signed off.
  return [...entries.values()].map((entry) =>
    entry.shelves.some((shelf) => shelf.status === 'OPEN') ? { ...entry, confirmedAt: null } : entry,
  );
};

/**
 * The monthly audit record [15]. Nothing here is valued: a round reports the lines it adjusted, never money —
 * the centre manages the adjustment count toward zero. One row is one audit, with every shelf it walked inside
 * it; a ✎ edit is its own row, and the type filter asks the server for one kind only.
 */
export const PastAuditLogsModal = ({
  rounds,
  loading,
  origin = 'all',
  hasMore = false,
  onOriginChange,
  onLoadMore,
  onClose,
}: PastAuditLogsModalProps) => {
  const dialog = useModalDialog(onClose);
  const entries = useMemo(() => groupRounds(rounds), [rounds]);

  return (
    <dialog
      className="inv-modal audit-log-modal"
      ref={dialog}
      aria-label="Past audit logs"
      onClick={(event) => event.target === event.currentTarget && onClose()}
    >
      <header>
        Past Audit Logs — stock take rounds
        <button type="button" className="x" aria-label="Close past audit logs" onClick={onClose}>
          ✕
        </button>
      </header>
      <div className="body">
        {onOriginChange && (
          <div className="audit-log-filters">
            <label>
              Type{' '}
              <select
                className="sel"
                aria-label="Round type"
                value={origin}
                disabled={loading}
                onChange={(event) => onOriginChange(event.target.value as RoundTypeFilter)}
              >
                <option value="all">All</option>
                <option value="STOCK_AUDIT">Audits</option>
                <option value="MANUAL_ADJUST">✎ Edits</option>
              </select>
            </label>
          </div>
        )}
        <table className="tbl">
          <thead>
            <tr>
              <th>Audit Date</th>
              <th>Started</th>
              <th>Auditor</th>
              <th>Type</th>
              <th>Shelves</th>
              <th>Status</th>
              <th>Adjustments</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => (
              <tr key={entry.key}>
                <td className="num">{auditDate(entry.confirmedAt ?? entry.startedAt)}</td>
                <td className="num">{auditDate(entry.startedAt)}</td>
                <td>{entry.operator ?? MISSING}</td>
                <td>{TYPE_LABEL[entry.origin]}</td>
                <td className="shelf-cell">
                  {entry.shelves.map((shelf) => (
                    <span
                      key={shelf.docId}
                      className="loc-pill"
                      title={`${shelf.status.toLowerCase()} · ${shelf.adjusted} adjusted`}
                    >
                      {shelfLabel(shelf.locationCode)}
                    </span>
                  ))}
                </td>
                <td>{entry.status}</td>
                <td className="num">{entry.adjusted}</td>
              </tr>
            ))}
            {entries.length === 0 && (
              <tr>
                <td colSpan={7} className="blocked-note">
                  {loading ? 'Loading the rounds…' : 'No stock take has been recorded yet.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {hasMore && onLoadMore && (
          <div className="audit-log-more">
            <button type="button" className="btn btn-line btn-sm" disabled={loading} onClick={onLoadMore}>
              {loading ? 'Loading…' : 'Load older rounds'}
            </button>
          </div>
        )}
        <div className="note">
          Each adjusted line is also written to that sku&apos;s Stock History as an ADJUST event — search the sku to
          inspect one on its own.
        </div>
      </div>
      <div className="foot">
        <button type="button" className="btn btn-gray btn-sm" onClick={onClose}>
          Close
        </button>
      </div>
    </dialog>
  );
};
